import '@discovr/ui/globals.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  queryClient,
  AuthProvider,
  captureVisitId,
  captureClaimToken,
  sentryPrivacyOptions,
  replayPrivacyOptions,
  scrubReplayEvent,
  captureShopifyReturnShop,
} from '@discovr/common';
import { Toaster, AppErrorBoundary, TooltipProvider } from '@discovr/ui';
import { AuthGate } from '@discovr/ui/auth-gate';
import { IconContext } from '@discovr/ui/design-system/icons';
import { NotificationProvider } from '@discovr/common';
import * as Sentry from '@sentry/react';
import { runtimeConfig } from '@discovr/common/runtime-config';
import { router } from './router';
import { onBrandSignOut } from './auth/onBrandSignOut';

// Share-link funnel (Baseline Metrics spec, Part A). Must run before the router
// mounts: the protected-route guard rewrites the URL to /login?redirect=…, which
// buries ?vid= inside an encoded param where it is far messier to recover.
captureVisitId();

// Same ordering constraint, higher stakes: the Shopify claim token is a bearer
// credential. It must leave the URL before the router can fold it into
// /login?redirect=… and before Sentry (below) or Clarity (BrandLayout) read
// location.href.
captureClaimToken();

// The Shopify store domain (?shop=) is merchant data; same ordering, same reason.
captureShopifyReturnShop();

/**
 * Vite emits `vite:preloadError` when a dynamic import fails. In PRODUCTION
 * this is almost always version skew — a long-lived tab holding an old
 * `index.html` that references hashed chunks the latest deploy no longer ships;
 * reloading picks up the fresh manifest. preventDefault swallows the error so
 * AppErrorBoundary doesn't flash before the reload; the sessionStorage debounce
 * caps it to one reload per 10s so a broken deploy can't trap the tab.
 *
 * DEV is excluded: there are no hashed chunks in dev, and a dev import failure
 * is a transient artifact of Vite re-optimizing deps on first visit to a route.
 * Vite handles that with its own (one-time, logged) reload — our silent reload
 * here turned every cold-chunk navigation into a jarring full-page reload.
 *
 * See: https://v7.vite.dev/guide/build.html#load-error-handling
 */
if (import.meta.env.PROD) {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    const KEY = 'vite-preload-reloaded-at';
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  });
}

const sentryDsn = runtimeConfig('SENTRY_DSN');
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: runtimeConfig('APP_ENV') ?? 'production',
    ...sentryPrivacyOptions,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(replayPrivacyOptions),
      // Forward console.error / console.warn to Sentry Logs (enableLogs below).
      Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/api-dev\.getdiscovr\.in/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  });
  Sentry.addEventProcessor(scrubReplayEvent);
}


/**
 * Bridges Firebase auth into the router context. Gates the router mount on
 * `loading`: until Firebase resolves the session we render <PageLoader/> and
 * do NOT mount <RouterProvider> — otherwise the protected route's beforeLoad
 * would run while the user is momentarily "unauthenticated" and bounce a
 * logged-in user to /login. Must live inside <AuthProvider> so useAuth works.
 */
function InnerApp() {
  return <AuthGate>{(isAuthenticated) => <RouterProvider router={router} context={{ auth: { isAuthenticated } }} />}</AuthGate>;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Discovr Brand: missing <div id="root"></div> in index.html');

createRoot(rootEl, {
  // React 19 error hooks → Sentry (the @sentry/react v10 standard). onCaughtError
  // fires for every AppErrorBoundary (incl. the silent fallback={null} ones);
  // onUncaughtError for errors no boundary catches; onRecoverableError for
  // React-recovered errors. AppErrorBoundary still renders the fallback UI.
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    {/* Figma icons use the light Phosphor weight — set it as the global default
        so every icon matches the design (heavier 'regular' looked too bold). */}
    <IconContext.Provider value={{ weight: 'light' }}>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
        <AuthProvider appSurface="brand_owner" onSignOut={onBrandSignOut}>
          <NotificationProvider>
            <InnerApp />
            {/* offset.bottom clears the app's fixed bottom-right action bars (e.g.
                the campaign wizard's Cancel/Continue footer) — the default 24px
                sonner offset sits toasts directly on top of those buttons,
                intercepting clicks. */}
            <Toaster position="bottom-right" offset={{ bottom: 88 }} />
          </NotificationProvider>
        </AuthProvider>
        </TooltipProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </AppErrorBoundary>
    </IconContext.Provider>
  </StrictMode>,
);
